import { useState } from "react";
import { Mail, MapPin, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import NomCharacter from "@/components/NomCharacter";
import { COMPANY } from "@/lib/site";

export default function Contact() {
  const [copied, setCopied] = useState(false);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(COMPANY.email);
      setCopied(true);
      toast.success("Email address copied to clipboard");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy — the address is " + COMPANY.email);
    }
  };

  return (
    <section id="contact" className="scroll-mt-24 pb-24 pt-8 sm:pb-28" data-testid="contact-section">
      <div className="mx-auto max-w-6xl px-6">
        <Reveal>
          <div className="relative overflow-hidden rounded-[40px] border border-nom-border bg-white p-8 shadow-nom sm:p-12 lg:p-16">
            <div aria-hidden="true" className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-nom-coral/10 blur-3xl" />
            <div className="relative grid items-center gap-10 md:grid-cols-12">
              <div className="md:col-span-8">
                <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-nom-coral" data-testid="contact-eyebrow">
                  Contact
                </p>
                <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight text-nom-ink sm:text-4xl">
                  Say hello.
                </h2>
                <p className="mt-4 max-w-lg text-base leading-relaxed text-nom-inkSoft sm:text-lg" data-testid="contact-body">
                  Questions about {COMPANY.legalName}, NomNom, support or anything else — email us and we'll get back to
                  you.
                </p>
                <div className="mt-7 flex flex-wrap items-center gap-3">
                  <a
                    href={`mailto:${COMPANY.email}`}
                    data-testid="contact-email-button"
                    className="inline-flex items-center gap-2.5 rounded-full bg-nom-coral px-7 py-4 font-display text-base font-semibold text-white shadow-coral transition-all duration-200 hover:-translate-y-0.5 hover:bg-nom-coralDark active:scale-[0.98] sm:text-lg"
                  >
                    <Mail className="h-5 w-5" aria-hidden="true" />
                    {COMPANY.email}
                  </a>
                  <button
                    type="button"
                    onClick={copyEmail}
                    data-testid="contact-copy-email-button"
                    className="inline-flex items-center gap-2 rounded-full border border-nom-borderStrong bg-white px-5 py-3.5 font-display text-sm font-semibold text-nom-ink transition-all duration-200 hover:-translate-y-0.5 hover:border-nom-coral hover:text-nom-coral active:scale-[0.98]"
                    aria-label="Copy email address to clipboard"
                  >
                    {copied ? <Check className="h-4 w-4 text-nom-success" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
                    {copied ? "Copied" : "Copy email"}
                  </button>
                </div>
                <p className="mt-3 text-xs font-bold text-nom-muted" data-testid="contact-email-hint">
                  Clicking the email button opens your mail app — if nothing happens, use "Copy email" and paste it into
                  your inbox.
                </p>
                <p
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-nom-muted"
                  data-testid="contact-location"
                >
                  <MapPin className="h-4 w-4 text-nom-coral" aria-hidden="true" />
                  {COMPANY.location}
                </p>
              </div>
              <div className="flex justify-center md:col-span-4">
                <div className="dot-grid flex w-full max-w-[240px] items-end justify-center rounded-[32px] bg-nom-peach/70 px-8 pb-0 pt-8">
                  <NomCharacter className="h-36 w-36 translate-y-3" bob={true} />
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
